export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts?.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

const DEFAULT_CONVERSATION_ASSIGNMENT_PERMISSIONS = {
  assign_agent: true,
  unassign_agent: true,
  self_assign: true,
  assign_team: true,
  unassign_team: true,
};
const DEFAULT_CONVERSATION_ASSIGNMENT_TEAM_SCOPE = {
  mode: 'all',
  team_ids: [],
};

export const getConversationAssignmentPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.can_assign_conversations === false) {
    return Object.fromEntries(
      Object.keys(DEFAULT_CONVERSATION_ASSIGNMENT_PERMISSIONS).map(key => [
        key,
        false,
      ])
    );
  }

  return {
    ...DEFAULT_CONVERSATION_ASSIGNMENT_PERMISSIONS,
    ...(currentAccount.conversation_assignment_permissions || {}),
  };
};

export const canAssignConversations = (user, accountId) => {
  return Object.values(
    getConversationAssignmentPermissions(user, accountId)
  ).some(Boolean);
};

export const getConversationAssignmentTeamScope = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return {
    ...DEFAULT_CONVERSATION_ASSIGNMENT_TEAM_SCOPE,
    ...(currentAccount.conversation_assignment_team_scope || {}),
  };
};

export const filterAssignableTeamsByScope = (teams = [], scope = {}) => {
  if (scope.mode === 'all') return teams;

  const teamIds = (scope.team_ids || []).map(Number);
  return teams.filter(team => teamIds.includes(Number(team.id)));
};

export const filterAssignableAgentsByTeam = (agents = [], teamId = null) => {
  if (!teamId) return agents;

  return agents.filter(agent => {
    if (agent.assignee_type && agent.assignee_type !== 'User') return true;
    return (agent.team_ids || []).map(Number).includes(Number(teamId));
  });
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};
